import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Table, Button, Card, Modal, Form, Input, Space, Popconfirm, Typography, Spin, Select, Checkbox } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../stores/root-store-context'
import { Currency } from '../../../api/FinanceApiClient'

const { Option } = Select

const CurrencyManagement = observer(() => {
  const { t } = useTranslation()
  const { financeStore } = useStore()

  const [modal, setModal] = useState(false)
  const [editing, setEditing] = useState<Currency | null>(null)
  const [available, setAvailable] = useState<{ code: string; name: string }[]>([])
  const [form] = Form.useForm()

  useEffect(() => {
    financeStore.getCurrencies()
    fetch('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies.json')
      .then(r => r.json())
      .then(j => setAvailable(Object.entries(j).map(([c, n]) => ({ code: c.toUpperCase(), name: n as string }))))
  }, [financeStore])

  const openAdd = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ isMain: financeStore.currencies.length === 0 })
    setModal(true)
  }

  const openEdit = (c: Currency) => {
    setEditing(c)
    form.setFieldsValue(c)
    setModal(true)
  }

  const save = async () => {
    const v = await form.validateFields()
    if (editing) await financeStore.updateCurrency(editing.id, v)
    else await financeStore.addCurrency(v)
    setModal(false)
  }

  const disableCheckbox =
    financeStore.currencies.length === 0 || (!!editing && financeStore.currencies.length === 1)

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '8%' },
    { title: t('Name'), dataIndex: 'name', key: 'name', width: '25%' },
    { title: t('Symbol'), dataIndex: 'symbol', key: 'symbol', width: '12%' },
    { title: t('ShortName'), dataIndex: 'shortName', key: 'shortName', width: '12%' },
    { title: t('Main'), dataIndex: 'isMain', key: 'isMain', width: '10%', render: (v: boolean) => (v ? '✓' : '') },
    {
      title: t('Actions'),
      key: 'a',
      width: '20%',
      render: (_: unknown, r: Currency) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title={t('DeleteCurrencyConfirm')} onConfirm={() => financeStore.deleteCurrency(r.id)}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <Spin spinning={financeStore.isLoading}>
      <Card
        title={<Typography.Title level={4}>{t('CurrencyManagement')}</Typography.Title>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            {t('AddCurrency')}
          </Button>
        }
      >
        <Table dataSource={financeStore.currencies} columns={columns} rowKey="id" pagination={false} />

        <Modal title={editing ? t('EditCurrency') : t('AddCurrency')} open={modal} onOk={save} onCancel={() => setModal(false)} destroyOnClose
  okText={t("OK")}             
  cancelText={t("Cancel")}     >
          <Form form={form} layout="vertical">
            <Form.Item name="shortName" label={t('ShortName')} rules={[{ required: true, message: t('PleaseEnterCurrencyShortName') }]}>
              <Select
                showSearch
                onChange={c => form.setFieldsValue({ name: available.find(a => a.code === c)?.name })}
                filterOption={(i, o) => (o?.value as string).toUpperCase().includes(i.toUpperCase())}
              >
                {available.map(a => (
                  <Option key={a.code} value={a.code}>
                    {a.code}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="name" label={t('Name')} rules={[{ required: true, message: t('PleaseEnterCurrencyName') }]}>
              <Select
                showSearch
                onChange={n => form.setFieldsValue({ shortName: available.find(a => a.name === n)?.code })}
                filterOption={(i, o) => ((o?.label ?? '') as string).toLowerCase().includes(i.toLowerCase())}
              >
                {available.map(a => (
                  <Option key={a.code} value={a.name} label={a.name}>
                    {a.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="symbol" label={t('Symbol')} rules={[{ required: true, message: t('PleaseEnterCurrencySymbol') }]}>
              <Input placeholder="$" />
            </Form.Item>

            <Form.Item name="isMain" valuePropName="checked">
              <Checkbox disabled={disableCheckbox}>{t('MainCurrency')}</Checkbox>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </Spin>
  )
})

export default CurrencyManagement
